<template>
  <div class="panel panel-bordered">
    <div class="panel-heading">
      <h3 class="panel-title">Questions</h3>
      <div class="panel-actions panel-actions-keep">
        <button
          type="button"
          class="btn btn-round btn-primary btn-sm"
          :disabled="disableAddButton"
          @click="openCreateModal"
        >
          <i class="icon md-plus" aria-hidden="true"></i>
          Add Question
        </button>
      </div>
    </div>

    <div class="panel-body">
      <loading :active="isLoading" :is-full-page="false" loader="bars" />

      <div v-if="questions.length" class="question-list">
        <QuestionCard
          v-for="(question, index) in questions"
          :key="question.id"
          :index="index"
          :question="question"
          @edit="openEditModal(question)"
          @delete="deleteQuestion(question)"
        />
      </div>

      <div v-else class="empty-state">
        No questions yet. Add up to 10 questions for this Ufu environment.
      </div>
    </div>

    <modal :name="modalName" :width="720" height="auto" :clickToClose="false">
      <QuestionModal
        v-if="modalVisible"
        :code="code"
        :question="selectedQuestion"
        @close="handleModalClose"
      />
    </modal>
  </div>
</template>

<script>
import Loading from "vue-loading-overlay";
import "vue-loading-overlay/dist/vue-loading.css";

import { gameSamuraiService } from "@/api/client";
import QuestionCard from "@/views/samurai/Settings/components/QuestionCard.vue";
import QuestionModal from "@/views/samurai/Settings/components/QuestionModal.vue";

export default {
  name: "QuestionList",

  components: {
    Loading,
    QuestionCard,
    QuestionModal,
  },

  props: {
    code: {
      type: String,
      required: true,
    },
  },

  data() {
    return {
      questions: [],
      isLoading: false,
      modalName: "samurai-ufu-question-modal",
      modalVisible: false,
      selectedQuestion: null,
    };
  },

  computed: {
    disableAddButton() {
      return this.questions.length >= 10;
    },
  },

  watch: {
    code: {
      immediate: true,
      handler() {
        this.fetchQuestions();
      },
    },
  },

  methods: {
    async fetchQuestions() {
      if (!this.code) return;

      this.isLoading = true;

      try {
        const questions = await gameSamuraiService.getQuestions(this.code);
        this.questions = Array.isArray(questions) ? questions : [];
      } catch (error) {
        error.response
          ? this.$dialogs.alert(error.response.data)
          : this.$dialogs.alert(error.message || error);
      } finally {
        this.isLoading = false;
      }
    },

    openCreateModal() {
      this.selectedQuestion = null;
      this.modalVisible = true;
      this.$modal.show(this.modalName);
    },

    openEditModal(question) {
      this.selectedQuestion = Object.assign({}, question);
      this.modalVisible = true;
      this.$modal.show(this.modalName);
    },

    async deleteQuestion(question) {
      const options = { cancelLabel: "No", okLabel: "Yes", size: "sm" };
      const { ok } = await this.$dialogs.confirm(
        `Delete question #${question.id} ?`,
        options
      );

      if (!ok) return;

      try {
        await gameSamuraiService.deleteQuestion(this.code, question.id);
        this.$notify("Delete question success.");
        await this.fetchQuestions();
      } catch (error) {
        error.response
          ? this.$dialogs.alert(error.response.data)
          : this.$dialogs.alert(error.message || error);
      }
    },

    async handleModalClose({ refresh }) {
      this.modalVisible = false;
      this.$modal.hide(this.modalName);
      this.selectedQuestion = null;

      if (refresh) {
        await this.fetchQuestions();
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.question-list {
  gap: 1rem;
  display: grid;
}

.empty-state {
  padding: 1rem;
  text-align: center;
  color: #76838f;
  background: #f8fafc;
  border-radius: 10px;
}
</style>
